import React, {cloneElement, useContext, useEffect, useState} from 'react';
import {oceanImportIsfList, TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import constants from '../../../constants';
import {Search} from "../../../filtering";
import {
  DateRenderer,DateTimeRenderer,FloatRenderer,NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import JobListViewContext, {IJobListViewContext, RightClickContext} from '../../common/JobListViewContext';
import {ActionButtonItem, borderColors, splitMenuItemsIntoSections} from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import PrimaryButton from '../../../ui/PrimaryButton';
import IfmsModal from '../../../ui/IfmsModal';
import {JobNames} from '../../../framework.core/JobNames';
import * as XCol from '../../../framework.core/XExportColumn';
import Menu, {IfmsMenuItem} from '../../../ui/menu/Menu';
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import { FmsSubMenu } from '../../../constants/fmsSubMenu';
import { IsfListItem } from '../../../fms.data/models/IsfListItem';
import InfoAreaDrawer from '../../../uiControls/infoArea/InfoAreaDrawer';
import { SuntekSettings, SystemSettings } from '../../../ifmsTypes';
import ApplicationContext from '../../../ApplicationContext';
import { useHistory } from 'react-router';
import { openNewPage, NavigationType } from '../../../util/navigationHelpers';
import useReportViewer from '../../../hooks/useReportViewer';
import { ISFHistoryArgs } from '../../../fms.report/args';
import { DocumentType } from '../../../framework.core/DocumentType';

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const jobListContext = useContext(JobListViewContext);

  const items = [
    { label: 'Search', shortcut: 'F5', separator: false, newCol: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch('F5');
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();

  const selectedElement = jobListContext.selectedElement as IsfListItem;

  async function moveToUsageReport() {
    if (!selectedElement) {
      return;
    }

    const viewUrl = `/isf/isfUsage`;

    openNewPage(history, viewUrl, NavigationType.newPage);
  }
  return {moveToUsageReport};
}

function IsfListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const [selectedPopupMenuItem, setSelectedPopupMenuItem] = useState<ActionButtonItem|null>(null);
  const { viewReport } = useReportViewer();

  const {moveToUsageReport} = useSelectedOperations();

  const selectedElement = jobListContext.selectedElement as IsfListItem;

  const onPopupClose = () => {
    setSelectedPopupMenuItem(null);
  }
  
  async function onSubMenuClick(fmsSubMenu: FmsSubMenu) {
    setSelectedPopupMenuItem(null);

    if (!selectedElement) {
      return;
    }
  }

    async function print() {
      if (!selectedElement) {
        return;
      }
  
      const title = `ISF History [${selectedElement?.hblNo}]`;
      const args = new ISFHistoryArgs({
        docType: DocumentType.ISFHistory,
        formCaption: title,
        subject: title,
        jobId: selectedElement?.id ?? 0,
        isfNo: selectedElement?.isfNo
      });
      viewReport(args, "", true);
  }

  async function handleIsfLink(url: string = '') {
    try {
      const apiResult = await appContext.api.getIsfLink(url);

      if (apiResult && apiResult.success) {
        window.open('https://imanifest.suntekscm.com/Home/AutoLoginFms?args=' + apiResult.result);
      }
    } catch (error) {
      console.error('ISF Filing Error:', error);
    }
  }

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/template/Suntek ISF Filing Template.xlsx'; // Relative path to your public file
    link.download = 'Suntek ISF Filing Template.xlsx'; // Suggested filename for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
      <div className="ifms-common-no-vertical-separator">
        <PrimaryButton name='ifs-list-filingStatus-button' size="regular" onClick={print}>ISF Detail</PrimaryButton>&nbsp;
        <PrimaryButton name='ifs-list-UsageReport-button' size="regular" onClick={moveToUsageReport}>ISF Usage</PrimaryButton>&nbsp;
        <PrimaryButton name='ifs-list-amsFiling-button' size="regular" onClick={() => handleIsfLink()}>ISF Filing</PrimaryButton>&nbsp;
        <PrimaryButton name='isf-list-isfUpload-button' size="regular" onClick={() => handleIsfLink('upload')}>ISF Upload</PrimaryButton>&nbsp;
        <PrimaryButton name='isf-list-xldownload-button' size="regular" onClick={downloadTemplate}>Std. ISF Excel Form</PrimaryButton>
      </div>
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.popupComponent ? (
        <IfmsModal isBlueHeader cancelOnEsc onCancel={onPopupClose} draggable isOpen={!!selectedPopupMenuItem} title={selectedPopupMenuItem?.popupLabel || selectedPopupMenuItem?.label} width={`${selectedPopupMenuItem?.popupWidth || 760}px`}>
          {cloneElement(selectedPopupMenuItem.popupComponent!, {onPopupClose})}
        </IfmsModal>
      ) : null}
      {!!selectedPopupMenuItem && !!selectedPopupMenuItem.subMenu ? onSubMenuClick(selectedPopupMenuItem.subMenu) : null}
    </div>
  );
}

function IsfListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function formatData(data:Array<IsfListItem>) {
  return data.map(d => ({
    ...d,
  }));
}

function IsfListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);

  const [unprocessedFkey, setUnprocessedFkey] = useState<string|null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }
    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey:string) {
    if (['F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      //hideLogHistory={true}
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.ISF}
      jobId={(jobListContext.selectedElement as IsfListItem|null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

/**
 * Concrete implementation for ISF List screen
 *
 * @class IsfListView
 * @extends {AbstractListView}
 */
 class IsfListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(JobNames.ISF, 'IsfListItem',
    IsfListItem.calculateSearchParameters(search, IsfListItem.MapSearchFields, []),
      1,
      search.pageSize);

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0 ) {
      return {data: [], totalCount: 0};
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): ListRowRenderer[] {
    const useBranch = systemSettings?.enableBranch ?? false;
    return IsfListItem.getExportCollection(useBranch).map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.dataIndex === "stat") {
        colRenderer.Cell = () => <span></span>
        colRenderer.Header = () => <span></span>;
      }
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.type === XCol.XCellType.Double) colRenderer.Cell = FloatRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status']}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'u2Date',
      orderByAscending: false,
    };
  }

  getTableSettings(opType:string, systemSettings:SystemSettings, suntekSettings:SuntekSettings): TableSettings {
    const tsettings = oceanImportIsfList;
    const useBranch = systemSettings?.enableBranch ?? false;
    const cols = IsfListItem.getExportCollection(useBranch);

    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
    
  }

  renderCommands() {
    return (
      <IsfListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <IsfListToolbar />
    );
  }


  renderExtras() {
    return <IsfListViewExtras />
  }

  getViewUrlFor(row: IsfListItem) {
    return '';
  }

  getListViewTitle() {
    return 'ISF List';
  };

  renderRowExtras(rowId: number | null, context: IJobListViewContext): React.ReactNode|null {
    return null;
  }

  renderAdditionalCommands() {
    return null;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
};

export default IsfListView;
